import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { toast } from 'react-toastify'
import * as guruChatService from 'services/GuruChatService'
import { writeGuruPreferences } from 'features/guru/guruPreferences'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Description = styled.p`
  margin: 0 0 14px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.5;
`

const SettingRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};

  &:last-child { border-bottom: none; }
`

const SettingLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SettingTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const SettingHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const initialSettings = {
  showCriticalPeeks: true,
  showMediumBadge: true,
  showInfoCards: true,
}

const GuruSettingsPanel = () => {
  const [settings, setSettings] = useState(initialSettings)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    guruChatService.getRecommendationSettings()
      .then(({ data }) => {
        if (cancelled) return
        const payload = data?.data || data || {}
        const next = {
          showCriticalPeeks: payload.showCriticalPeeks ?? payload.show_critical_peeks ?? true,
          showMediumBadge:   payload.showMediumBadge   ?? payload.show_medium_badge   ?? true,
          showInfoCards:     payload.showInfoCards     ?? payload.show_info_cards     ?? true,
        }
        setSettings(next)
        writeGuruPreferences(next)
        setLoaded(true)
      })
      .catch(() => { if (!cancelled) setLoaded(true) })
    return () => { cancelled = true }
  }, [])

  const update = (key) => async (e) => {
    const value = e.target.checked
    const previous = settings[key]
    const optimistic = { ...settings, [key]: value }
    setSettings(optimistic)
    writeGuruPreferences(optimistic)
    setSaving(true)
    try {
      await guruChatService.updateRecommendationSettings({ [key.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`)]: value })
    } catch (err) {
      const rolled = { ...settings, [key]: previous }
      setSettings(rolled)
      writeGuruPreferences(rolled)
      toast.error('Could not save Guru preference. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Wrap>
      <Description>
        Control how Guru, your Building Passport assistant, signals new recommendations.
      </Description>

      <SettingRow>
        <SettingLabel>
          <SettingTitle>Show critical alerts</SettingTitle>
          <SettingHint>
            A brief pop-out next to the Guru launcher when there is something urgent, such as overdue remedials, expiring certificates or unresolved incidents.
          </SettingHint>
        </SettingLabel>
        <FormControlLabel
          control={<Switch checked={settings.showCriticalPeeks} onChange={update('showCriticalPeeks')} disabled={!loaded || saving} />}
          label=""
          sx={{ marginRight: 0 }}
        />
      </SettingRow>

      <SettingRow>
        <SettingLabel>
          <SettingTitle>Show recommendation badge</SettingTitle>
          <SettingHint>
            The number badge on the Guru launcher for medium-priority items (informational suggestions are never counted).
          </SettingHint>
        </SettingLabel>
        <FormControlLabel
          control={<Switch checked={settings.showMediumBadge} onChange={update('showMediumBadge')} disabled={!loaded || saving} />}
          label=""
          sx={{ marginRight: 0 }}
        />
      </SettingRow>

      <SettingRow>
        <SettingLabel>
          <SettingTitle>Show informational suggestions</SettingTitle>
          <SettingHint>
            Ambient cards inside the Guru widget — compliance snapshots, summarise activity, etc. Disabling this hides them while keeping urgent items.
          </SettingHint>
        </SettingLabel>
        <FormControlLabel
          control={<Switch checked={settings.showInfoCards} onChange={update('showInfoCards')} disabled={!loaded || saving} />}
          label=""
          sx={{ marginRight: 0 }}
        />
      </SettingRow>
    </Wrap>
  )
}

export default GuruSettingsPanel
